import { DEFAULT_COIN_LABEL } from './coin-label';

/**
 * Friendly copy for the checkout / release error codes the API returns.
 *
 * Lives here rather than in `pages/pos.tsx` because the releasing queue needs it
 * too, and a page module that exports a non-component breaks Fast Refresh for
 * the whole file.
 *
 * `coinLabel` is the tenant's name for the coin wallet (`useCoinLabel()`), so a
 * renamed tenant reads "insufficient UPCoin balance". It defaults because only
 * the checkout can raise a coin code — the releasing and consignment callers
 * never see one and have no reason to hold the label.
 */
export function prettyError(code: string, coinLabel: string = DEFAULT_COIN_LABEL): string {
  switch (code) {
    case 'out_of_stock':
      return 'Out of stock. Refresh the product list.';
    case 'insufficient_payment':
      return 'Tendered amount is less than the total.';
    case 'period_closed':
      return 'The current pay period is closed. Ask admin to open the next period.';
    case 'branch_scope_violation':
      return 'You are not authorized for this branch.';
    case 'branch_required':
      return 'Pick a branch first — this document needs one.';
    case 'tendered_must_equal_total_for_non_cash':
      return 'Card / wallet payment must equal the total.';
    case 'payment_method_not_allowed_for_role':
      return 'Your role can not accept this payment method.';
    case 'kit_code_already_used':
      return 'Cannot void: a kit code from this order has already been redeemed.';
    case 'order_not_releasable':
      return 'This order is not awaiting release.';
    case 'order_already_released':
      return 'This order was already released.';
    case 'slot_required_for_non_cash':
      return `The member needs an active slot to pay with GC / ${coinLabel}.`;
    case 'insufficient_gc':
      return 'Member has insufficient GC balance.';
    case 'insufficient_coin':
      return `Member has insufficient ${coinLabel} balance.`;
    case 'product_not_purchasable_in_gc':
      return 'An item is not sold in GC.';
    case 'product_not_purchasable_in_coin':
      return `An item is not sold in ${coinLabel}.`;
    case 'unknown_custom_fee_type':
      // Covers both "never existed" and "an admin just retired it" — the same
      // answer either way to the cashier standing at the till.
      return 'That custom fee is no longer available. Refresh and pick another.';
    case 'duplicate_custom_fee_type':
      return 'The same custom fee was added twice. Remove one.';
    case 'custom_fee_not_allowed_for_non_cash':
      return `Custom fees are cash only — remove them to pay with GC / ${coinLabel}.`;
    case 'custom_fee_negative':
      return 'A fee cannot be negative. Use the cashier discount to reduce a sale.';
    // Both mean the till's fee list is older than the admin's registry — a fee
    // changed between "cashier types it" and a fixed rate. Refreshing reloads
    // the types and the row prices itself correctly.
    case 'custom_fee_amount_not_accepted':
      return 'That fee is now set by an admin. Refresh to pick up the new amount.';
    case 'custom_fee_amount_required':
      return 'That fee needs an amount. Refresh, then type it in.';
    // Accounting v1 — consignment codes. Some arrive suffixed with the product
    // id (`insufficient_consigned_stock:<id>`); callers strip before lookup or
    // fall through to the prefix cases below.
    case 'consignment_disabled':
      return 'Consignment is not enabled for this store.';
    case 'insufficient_consigned_stock':
      return 'The consignee does not hold that many units of an item. Refresh their holdings.';
    case 'consignment_partially_consumed':
      return 'Some units on this consignment were already billed or returned — it can no longer be voided. Use a return or a void on the later documents instead.';
    case 'invoice_has_payments':
      return 'This billing has payments applied. Void its payment records first.';
    case 'allocation_exceeds_outstanding':
      return 'A payment line is larger than that billing’s remaining balance.';
    case 'allocation_sum_mismatch':
      return 'The applied lines must add up to the payment amount exactly.';
    case 'invoice_already_paid':
      return 'That billing is already fully paid.';
    case 'already_void':
      return 'This document is already void.';
    case 'duplicate_line':
      return 'The same product appears twice. Combine the lines.';
    // LMS — course lines at the till. A POS sale always books to the selected
    // member, so all three are about WHO the access would land on.
    case 'course_already_enrolled':
      return 'This member already owns that course. Remove it from the cart.';
    case 'creator_course_requires_creator_order':
      return 'That course is sold by its instructor — the member buys it from the storefront.';
    case 'course_sales_disabled_at_pos':
      return 'Courses are not sold at the till. The member buys this from the storefront.';
    case 'course_unavailable':
      return 'That course is not published yet, so it cannot be sold.';
    case 'course_qty_must_be_one':
      return 'A course is bought once — set its quantity to 1.';
    default:
      return code;
  }
}
